import "./WatchListComponent.scss";
import template from "./WatchListComponent.html";
import {IComponentOptions, IOnDestroy, IRootElementService} from "angular";
import {BehaviorSubject, Observable, ReplaySubject} from "rxjs";
import {ComponentCallback} from "../../../types/AngularJS";
import {WatchListStore} from "../../store/WatchListStore";
import {WatchlistSummary} from "../../models/WatchlistSummary";
import {WatchlistItemViewModel, WatchlistItemViewModels, WatchlistViewModel} from "../../models/WatchlistViewModel";
import {WatchlistId} from "../../models/WatchlistId";
import {CodeAndExchange} from "app/instruments/types/CodeAndExchange";
import {isNotNull} from "../../../js/util/UtilFunctions";
import {ObjectUtil} from "../../../js/util/ObjectUtil";
import {WatchlistSortColumn} from "../../models/WatchlistSort";
import {SortingDirection} from "../../../components/ui/sort-indicator/SortingDirection";
import {Subject} from "rxjs/Subject";

export const WatchList = "tfWatchList";

export function watchList(): IComponentOptions {
    return {
        template,
        bindings: {
            includeBidAsk: "<",
            selected: "<",
            itemIsHref: "<",
            onChange: "&",
            onClick: "&"
        },
        controllerAs: "ctrl",
        controller: WatchListComponentController
    };
}

export class WatchListComponentController implements IOnDestroy {

    /** @input */
    public set selected(id: WatchlistId | null) {
        if (id != null) {
            this.selected$.next(id);
        }
    }

    private readonly selected$ = new ReplaySubject<WatchlistId>(1);

    /** @input */
    public readonly itemIsHref: boolean;

    private hasOnClick: boolean = false;

    /** @input */
    public readonly onChange: ComponentCallback;

    /** @input */
    public readonly onClick: ComponentCallback;

    /** @input */
    public readonly includeBidAsk: boolean = false;

    public readonly currentWatchlist$: Observable<WatchlistSummary | null>;

    public readonly asAt$ = new ReplaySubject<Date>(1);
    public readonly watchlistItems$: Observable<WatchlistItemViewModels>;
    public readonly newItemVisible$ = new BehaviorSubject(false);
    public readonly codeSortDirection$: Observable<SortingDirection | null>;
    public readonly changeSortDirection$: Observable<SortingDirection | null>;

    private readonly model$: Observable<WatchlistViewModel | null>;
    private readonly toggleSorting$ = new Subject<WatchlistSortColumn>();
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly watchListStore: WatchListStore,
                       $element: IRootElementService) {
        this.hasOnClick = $element.attr("on-click") != null;
        this.currentWatchlist$ = this.buildCurrentWatchlist();
        this.model$ = this.buildModel();
        this.watchlistItems$ = this.buildWatchlistItems();
        this.codeSortDirection$ = this.buildCodeSortDirection();
        this.changeSortDirection$ = this.buildChangeSortDirection();

        this.watchToggleSorting();
    }

    private buildCurrentWatchlist(): Observable<WatchlistSummary | null> {
        return Observable.combineLatest(this.selected$, this.watchListStore.watchlists)
                         .takeUntil(this.destroyed$)
                         .map(([selected, watchlists]) => watchlists.find((wl) => wl.id === selected)!);
    }

    private buildModel(): Observable<WatchlistViewModel | null> {
        return this.currentWatchlist$
                   .switchMap((current) => this.retrieveWatchList(current))
                   .do(() => this.asAt$.next(new Date()))
                   .publishReplay(1)
                   .refCount();
    }

    private retrieveWatchList(watchlist: WatchlistSummary | null): Observable<WatchlistViewModel | null> {
        if (watchlist == null) {
            return Observable.of(null);
        } else {
            return this.watchListStore.select(watchlist.id);
        }
    }

    private buildWatchlistItems(): Observable<WatchlistItemViewModels> {
        return this.model$.map((m) => m?.items ?? []);
    }

    private buildCodeSortDirection(): Observable<SortingDirection | null> {
        return this.model$
                   .filter(isNotNull)
                   .map((m) => m.sorting)
                   .map((sorting) => {
                       if (sorting == null) {
                           return null;
                       } else if (sorting.column === WatchlistSortColumn.CODE) {
                           return sorting.direction;
                       } else {
                           return null;
                       }
                   });
    }

    private buildChangeSortDirection(): Observable<SortingDirection | null> {
        return this.model$
                   .filter(isNotNull)
                   .map((m) => m.sorting)
                   .map((sorting) => {
                       if (sorting == null) {
                           return null;
                       } else if (sorting.column === WatchlistSortColumn.CHANGE) {
                           return sorting.direction;
                       } else {
                           return null;
                       }
                   });
    }

    private watchToggleSorting() {
        this.toggleSorting$
            .withLatestFrom(this.currentWatchlist$)
            .takeUntil(this.destroyed$)
            .subscribe(([column, watchlist]) => {
                if (watchlist != null) {
                    this.watchListStore.toggleSorting(watchlist.id, column);
                }
            });
    }

    public onSelect(watchlist: WatchlistSummary) {
        this.onChange({id: watchlist.id});
    }

    public itemClicked(item: WatchlistItemViewModel) {
        const security = ObjectUtil.pick(item, "code", "exchange");
        this.onClick({security});
    }

    public toggleSorting(column: WatchlistSortColumn) {
        this.toggleSorting$.next(column);
    }

    public showNewItem() {
        this.newItemVisible$.next(true);
    }

    public addItem(security: CodeAndExchange) {
        Observable.zip(this.currentWatchlist$, Observable.of(security))
                  .filter(([watchlist]) => isNotNull(watchlist))
                  .subscribe(([watchlist, sec]) => this.watchListStore.addItem(watchlist!.id, sec));
        this.newItemVisible$.next(false);
    }

    public removeItem(item: WatchlistItemViewModel) {
        Observable.zip(this.currentWatchlist$, Observable.of(item))
                  .filter(([watchlist]) => isNotNull(watchlist))
                  .subscribe(([watchlist, security]) => this.watchListStore.removeItem(watchlist!.id, security));
    }

    public addItemBlurred() {
        this.newItemVisible$.next(false);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
